import React, { useMemo } from 'react';
import { Button } from '@saleshandy/design-system';
import { Mail } from '@saleshandy/icons';
import {
  OverlayTooltip,
  Placement,
} from '../../../../../shared/design-system/components/overlay';
import { V2Lead } from '../../../types';
import EmailListWithCopy from '../../profile-modal/shared/email-list-with-copy';
import { LeadRevealFeedbackSentiment } from '../../../helpers/leads.api';

interface EmailCellProps {
  lead: V2Lead;
  onRevealEmail: (leadId: number) => void;
  isRevealing?: boolean;
  isAnyRevealing?: boolean;
  isAlreadyRevealed?: boolean;
  feedbackSentiment?: 'positive' | 'negative';
  feedbackMessage?: string;
  onFeedbackSubmitted?: (
    sentiment: LeadRevealFeedbackSentiment,
    message?: string,
  ) => void;
}

const EmailCell: React.FC<EmailCellProps> = ({
  lead,
  onRevealEmail,
  isRevealing = false,
  isAnyRevealing = false,
  isAlreadyRevealed = false,
  feedbackSentiment,
  feedbackMessage,
  onFeedbackSubmitted,
}) => {
  const { emails, hasEmails } = useMemo(() => {
    const emailsData: { email: string; shVerificationStatus: string }[] =
      lead?.emails || [];
    return {
      emails: emailsData,
      hasEmails: emailsData.length > 0,
    };
  }, [lead?.emails]);

  // Has data → show with inline feedback visible on CSS :hover
  if (hasEmails) {
    return (
      <EmailListWithCopy
        emails={emails}
        showVerification
        feedbackProps={{
          leadId: lead?.id,
          initialSentiment: feedbackSentiment,
          initialMessage: feedbackMessage,
          onFeedbackSubmitted,
        }}
      />
    );
  }

  // Currently enriching (check before isAlreadyRevealed since a re-reveal may be in progress)
  if (isRevealing) {
    return (
      <div className="v2-lead-email-cell">
        <OverlayTooltip placement={Placement.Bottom} text="Enriching Lead...">
          <Button variant="secondary" disabled className="v2-lead-reveal-btn">
            <Mail width={16} height={16} />
            <span className="revealing-text">
              Enriching
              <div className="loader" />
            </span>
          </Button>
        </OverlayTooltip>
      </div>
    );
  }

  // Already revealed for this type but no data → not found
  if (isAlreadyRevealed) {
    return <div className="v2-lead-email-cell">-</div>;
  }

  // Not revealed yet → show reveal button
  return (
    <div className="v2-lead-email-cell">
      <OverlayTooltip
        placement={Placement.Bottom}
        text={isAnyRevealing ? 'Enriching Lead...' : '1 Credit Required'}
      >
        <Button
          variant="secondary"
          onClick={() => onRevealEmail(lead?.id)}
          disabled={isAnyRevealing}
          className="v2-lead-reveal-btn"
        >
          <Mail width={16} height={16} />
          View Email
        </Button>
      </OverlayTooltip>
    </div>
  );
};

export default React.memo(
  EmailCell,
  (prevProps, nextProps) =>
    prevProps.lead?.id === nextProps.lead?.id &&
    JSON.stringify(prevProps.lead?.emails) ===
      JSON.stringify(nextProps.lead?.emails) &&
    prevProps.isRevealing === nextProps.isRevealing &&
    prevProps.isAnyRevealing === nextProps.isAnyRevealing &&
    prevProps.isAlreadyRevealed === nextProps.isAlreadyRevealed &&
    prevProps.onRevealEmail === nextProps.onRevealEmail &&
    prevProps.feedbackSentiment === nextProps.feedbackSentiment &&
    prevProps.feedbackMessage === nextProps.feedbackMessage &&
    prevProps.onFeedbackSubmitted === nextProps.onFeedbackSubmitted,
);
